'use client';

import { ArrowDown, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { JSX } from 'react';

interface PullToRefreshIndicatorProps {
  /** Current pull distance in pixels */
  pullDistance: number;
  /** Threshold distance to trigger refresh */
  threshold: number;
  /** Whether refresh is in progress */
  isRefreshing: boolean;
}

/**
 * Visual indicator for pull-to-refresh gesture.
 * Shows an arrow that rotates as user pulls down, then a spinner during refresh.
 */
export function PullToRefreshIndicator({
  pullDistance,
  threshold,
  isRefreshing,
}: PullToRefreshIndicatorProps): JSX.Element | null {
  const t = useTranslations('pwa');

  // Don't render if not pulling and not refreshing
  if (pullDistance === 0 && !isRefreshing) {
    return null;
  }

  // Calculate rotation: 0° at start, 180° at threshold
  const rotation = Math.min((pullDistance / threshold) * 180, 180);
  const isPastThreshold = pullDistance >= threshold;

  return (
    <div
      className="flex flex-col items-center justify-center py-4 transition-opacity duration-200"
      style={{
        height: `${Math.max(pullDistance, isRefreshing ? threshold : 0)}px`,
        opacity: pullDistance > 10 || isRefreshing ? 1 : 0,
      }}
      role="status"
      aria-live="polite"
    >
      {isRefreshing ? (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="mt-2 text-xs text-muted-foreground">
            {t('pullToRefresh.refreshing')}
          </span>
        </>
      ) : (
        <>
          <ArrowDown
            className="h-6 w-6 text-muted-foreground transition-transform duration-100"
            style={{ transform: `rotate(${rotation}deg)` }}
          />
          <span className="mt-2 text-xs text-muted-foreground">
            {isPastThreshold ? t('pullToRefresh.release') : t('pullToRefresh.pullDown')}
          </span>
        </>
      )}
    </div>
  );
}
